import axios from "axios";

const inferredDefaultBaseUrl =
	typeof window !== "undefined" && window.location?.hostname === "localhost"
		? "http://localhost:8000"
		: "https://core.gamura.ai";

const API_BASE_URL = process.env.REACT_APP_API_URL || inferredDefaultBaseUrl;

const getAuthToken = () => localStorage.getItem("gamora_token");

const api = axios.create({
	baseURL: API_BASE_URL,
	headers: {
		"Content-Type": "application/json",
	},
});

// Add a request interceptor to include Authorization header
api.interceptors.request.use((config) => {
	const token = getAuthToken();
	if (token) {
		config.headers = config.headers || {};
		config.headers["Authorization"] = `Bearer ${token}`;
	}
	return config;
});

// Add response interceptor to handle 401 errors
api.interceptors.response.use(
	(response) => response,
	(error) => {
		// Only redirect on 401 errors for authenticated requests, not for login attempts
		if (
			error.response?.status === 401 &&
			!error.config?.url?.includes("/api/v1/auth/login")
		) {
			// Clear all auth data
			localStorage.removeItem("gamora_token");
			localStorage.removeItem("gamora_role");
			localStorage.removeItem("gamora_user_id");
			localStorage.removeItem("gamora_session_id");

			// Redirect to login page
			window.location.href = "/";
		}
		return Promise.reject(error);
	}
);

export interface SearchRequest {
	query: string;
	top_k: number;
	generate_answer?: boolean;
}

export interface Reference {
	url: string;
	country: string;
}

export interface DetectedForm {
	form_id: string;
	form_code: string;
	form_name: string;
	description: string;
	jurisdiction: string;
	has_form_file: boolean;
	form_download_url?: string;
	has_checklist: boolean;
	checklist_download_url?: string;
	has_md_checklist?: boolean;
	md_checklist_download_url?: string;
	has_ivd_checklist?: boolean;
	ivd_checklist_download_url?: string;
	source_page_url?: string;
}

export interface MDRCitation {
	type: 'Article' | 'Annex' | 'Chapter' | 'Section';
	number: string;
	paragraph?: string;
	section?: string;
	full_reference: string;
	context?: string;
	source_chunk?: string;
	full_text?: string;
	regulation?: string;
}

export interface EudamedDevicesRequest {
	page: number;
	pageSize: number;
	size: number;
	iso2Code: string;
	sort: string[];
	medicalPurposeProcedurePack: string;
	deviceStatusCode: string;
	languageIso2Code: string;
}

export interface SearchResponse {
	query: string;
	answer: string;
	references: Reference[] | string[];
	citations?: MDRCitation[]; // MDR section citations
	tokens_used: number;
	tokens_remaining: number;
	prompt_tokens: number;
	completion_tokens: number;
	token_breakdown?: {
		chunking_tokens: number;
		embedding_tokens: number;
		retrieval_tokens: number;
		generation_tokens: number;
		total_end_to_end: number;
	};
	generation_id?: string; // Add this field for feedback
	mdr_references?: string[];
	reference_count?: number;
	is_mdr_query?: boolean;
	forms?: DetectedForm[]; // Forms detected from the query
	forms_available?: number;
	checklists_available?: number;
}

export interface FeedbackRequest {
	generation_id: string;
	score: number;
	comment?: string;
}

export interface FeedbackResponse {
	success: boolean;
	feedback_id: string;
	message: string;
}

export interface FeedbackItem {
	feedback_id: string;
	generation_id: string;
	score: number;
	comment?: string;
	timestamp: string;
	user_id?: string;
	username?: string;
	feedback_type: string;
}

export interface FeedbackListResponse {
	feedbacks: FeedbackItem[];
	total_count: number;
	positive_count: number;
	negative_count: number;
}

// Utility functions for feedback management
export const getFeedbackKey = (generationId: string): string =>
	`feedback_${generationId}`;

export const hasFeedback = (generationId: string): boolean => {
	try {
		const feedback = localStorage.getItem(getFeedbackKey(generationId));
		return feedback !== null;
	} catch (e) {
		return false;
	}
};

export const getFeedback = (
	generationId: string
): { score: number; comment?: string; timestamp: string } | null => {
	try {
		const feedback = localStorage.getItem(getFeedbackKey(generationId));
		return feedback ? JSON.parse(feedback) : null;
	} catch (e) {
		return null;
	}
};

export const clearFeedback = (generationId: string): void => {
	try {
		localStorage.removeItem(getFeedbackKey(generationId));
	} catch (e) {
		// Silently fail if localStorage is not available
	}
};

export const clearAllFeedback = (): void => {
	try {
		// Get all localStorage keys that start with 'feedback_'
		const keys = Object.keys(localStorage);
		keys.forEach((key) => {
			if (key.startsWith("feedback_")) {
				localStorage.removeItem(key);
			}
		});
	} catch (e) {
		// Silently fail if localStorage is not available
	}
};

// Helper function to get regulatory body label from country
export const getRegulatoryBodyLabel = (country: string): string => {
	switch (country) {
		case "US":
		case "USA":
		case "United States":
			return "FDA (USA)";
		case "IN":
		case "India":
			return "CDSCO (India)";
		case "GB":
		case "UK":
		case "United Kingdom":
			return "MHRA (UK)";
		case "AU":
		case "Australia":
			return "TGA (Australia)";
		case "CA":
		case "Canada":
			return "Health Canada";
		case "EU":
			return "EU Regulations";
		default:
			return country;
	}
};

// Helper function to get unique regulatory bodies from references
export const getUniqueRegulatoryBodies = (
	references: Reference[] | string[]
): string[] => {
	// Handle both new format (Reference[]) and old format (string[])
	if (references.length > 0 && typeof references[0] === "string") {
		// Old format: just URLs, can't determine regulatory body
		return [];
	}

	const refs = references as Reference[];
	const countries = refs.map((ref) => ref.country).filter(Boolean);
	const uniqueCountries = Array.from(new Set(countries));
	return uniqueCountries.map((country) => getRegulatoryBodyLabel(country));
};

export interface EmbedRequest {
	urls: string[];
}

export interface EmbedResponse {
	status: "success" | "error";
	message?: string;
	error?: string;
	pages_processed?: number;
	total_chunks_processed?: number;
	final_collection_count?: number;
	processing_mode?: string;
	depth_coverage?: string;
	performance?: string;
}

export interface CollectionCountResponse {
	collection_name: string;
	count: number;
	status: string;
}

export interface CollectionStatItem {
	key: string;
	count: number;
}

export interface CollectionStatsResponse {
	collection_name: string;
	total_count: number;
	status: string;
	by_country: CollectionStatItem[];
	by_jurisdiction: CollectionStatItem[];
	by_source_type: CollectionStatItem[];
}

export interface StandardDocumentsStatsResponse {
	total_count: number;
	by_country: CollectionStatItem[];
	by_jurisdiction: CollectionStatItem[];
	by_regulatory_authority: CollectionStatItem[];
	by_status: CollectionStatItem[];
}

export interface ChatMessageIn {
	session_id: string;
	message: string;
	sender: string;
	user_id?: string;
	generation_id?: string; // Add generation_id for feedback
	forms?: DetectedForm[]; // Forms detected from the response
	references?: Array<{ url: string; country: string }> | string[]; // References from the response
	workflow_id?: string;
	workflow_name?: string;
}

export interface ChatMessageOut {
	id: number;
	session_id: string;
	message: string;
	sender: string;
	user_id?: string;
	generation_id?: string; // Add generation_id for feedback
	forms?: DetectedForm[]; // Forms detected from the response
	references?: Array<{ url: string; country: string }> | string[]; // References from the response
	workflow_id?: string;
	workflow_name?: string;
	timestamp: string;
}

export interface ChatLinkWorkflowIn {
	session_id: string;
	generation_id: string;
	workflow_id: string;
	workflow_name?: string;
}

export interface ChatLinkWorkflowOut {
	success: boolean;
}

export interface ChatTokenUsage {
	id: number;
	session_id: string;
	user_id?: string;
	message: string;
	sender: string;
	tokens_used: number;
	prompt_tokens: number;
	completion_tokens: number;
	total_tokens: number;
	chunking_tokens?: number;
	embedding_tokens?: number;
	retrieval_tokens?: number;
	generation_tokens?: number;
	total_end_to_end_tokens?: number;
	token_breakdown_json?: any;
	timestamp: string;
}

export interface ChatSession {
	session_id: string;
	name: string;
	timestamp: string;
}

export interface SignupRequest {
	username: string;
	email: string;
	password: string;
	company?: string;
}

export interface LoginRequest {
	username: string;
	password: string;
}

export interface AuthResponse {
	access_token?: string;
	token_type?: string;
	message?: string;
	user_id?: number;
	role?: number;
}

export interface User {
	id: string;
	username: string;
	email: string;
	role: number;
	company?: string;
	tokens_available: number;
	tokens_purchased: number;
	last_token_purchase?: string;
	created_at: string;
}

export interface UserUpdateRequest {
	username?: string;
	email?: string;
	password?: string;
	role?: number;
	company?: string;
	tokens_available?: number;
	tokens_purchased?: number;
}

export interface InvitationDetails {
	organization: {
		id: string;
		name: string;
		description?: string;
		industry?: string;
	};
	role: string;
	invited_by: string;
	message?: string;
	expires_at: string;
}

export const gamoraApi = {
	// Search for documents
	search: async (
		query: string,
		topK: number = 5,
		sessionId?: string,
		jurisdiction?: string,
		generateAnswer: boolean = true
	): Promise<SearchResponse> => {
		const body: any = { query, top_k: topK, generate_answer: generateAnswer };
		if (jurisdiction) body.jurisdiction = jurisdiction;
		const response = await api.post<SearchResponse>("/api/v1/search", body, {
			headers: {
				"X-Session-Id": sessionId || localStorage.getItem("gamora_session_id"),
			},
		});
		return response.data;
	},

	// Embed documents from URLs
	embed: async (urls: string[] = []): Promise<EmbedResponse> => {
		const response = await api.post<EmbedResponse>("/embed", {
			urls,
		});
		return response.data;
	},

	// Get collection count
	getCollectionCount: async (): Promise<CollectionCountResponse> => {
		const response = await api.get<CollectionCountResponse>(
			"/collection/count"
		);
		return response.data;
	},

	// Get collection stats
	getCollectionStats: async (): Promise<CollectionStatsResponse> => {
		const response = await api.get<CollectionStatsResponse>(
			"/collection/stats"
		);
		return response.data;
	},

	// Get standards stats (admin only)
	getStandardDocumentsStats: async (): Promise<StandardDocumentsStatsResponse> => {
		const response = await api.get<StandardDocumentsStatsResponse>(
			"/admin/standards/stats"
		);
		return response.data;
	},

	// Save a chat message
	saveChatMessage: async (msg: ChatMessageIn): Promise<ChatMessageOut> => {
		const response = await api.post<ChatMessageOut>(
			"/api/v1/chat/history",
			msg
		);
		return response.data;
	},

	// Link an existing assistant message to a workflow
	linkWorkflowToChatMessage: async (
		payload: ChatLinkWorkflowIn
	): Promise<ChatLinkWorkflowOut> => {
		const response = await api.post<ChatLinkWorkflowOut>(
			"/api/v1/chat/link-workflow",
			payload
		);
		return response.data;
	},

	// Get chat history for a session and user
	getChatHistory: async (session_id: string): Promise<ChatMessageOut[]> => {
		const params = { session_id };
		const response = await api.get<ChatMessageOut[]>("/api/v1/chat/history", {
			params,
		});
		return response.data;
	},

	// Get token usage for a session (and optional user)
	getChatTokens: async (
		session_id: string,
		user_id?: string
	): Promise<ChatTokenUsage[]> => {
		const params = user_id ? { session_id, user_id } : { session_id };
		const response = await api.get<ChatTokenUsage[]>("/api/v1/chat/tokens", {
			params,
		});
		return response.data;
	},

	// User signup
	signup: async (data: SignupRequest): Promise<AuthResponse> => {
		const response = await api.post<AuthResponse>("/api/v1/auth/signup", data);
		return response.data;
	},

	// User login
	login: async (data: LoginRequest): Promise<AuthResponse> => {
		const response = await api.post<AuthResponse>("/api/v1/auth/login", data);
		return response.data;
	},

	// Forgot password
	forgotPassword: async (email: string): Promise<{ message: string }> => {
		const response = await api.post<{ message: string }>("/api/v1/auth/forgot-password", {
			email,
		});
		return response.data;
	},

	// Reset password
	resetPassword: async (token: string, newPassword: string): Promise<{ message: string }> => {
		const response = await api.post<{ message: string }>("/api/v1/auth/reset-password", {
			token,
			new_password: newPassword,
		});
		return response.data;
	},

	// Fetch all chat sessions
	getAllChatSessions: async (): Promise<ChatSession[]> => {
		const response = await api.get<ChatSession[]>("/api/v1/chat/sessions");
		return response.data;
	},

	// Generate shareable link for a session
	generateShareLink: async (session_id: string): Promise<{
		share_url: string;
		share_token: string;
		session_id: string;
	}> => {
		const response = await api.post<{
			share_url: string;
			share_token: string;
			session_id: string;
		}>("/api/v1/chat/generate-share-link", null, {
			params: { session_id },
		});
		return response.data;
	},

	// Revoke shareable link for a session
	revokeShareLink: async (session_id: string): Promise<{ detail: string }> => {
		const response = await api.delete<{ detail: string }>(
			"/api/v1/chat/revoke-share-link",
			{
				params: { session_id },
			}
		);
		return response.data;
	},

	// Get shared session by token (public access)
	getSharedSession: async (share_token: string): Promise<{
		session_id: string;
		session_name: string;
		created_at: string | null;
		messages: Array<{
			id: string;
			message: string;
			sender: string;
			timestamp: string | null;
			metadata: any;
		}>;
	}> => {
		const response = await api.get<{
			session_id: string;
			session_name: string;
			created_at: string | null;
			messages: Array<{
				id: string;
				message: string;
				sender: string;
				timestamp: string | null;
				metadata: any;
			}>;
		}>(`/api/v1/chat/shared/${share_token}`);
		return response.data;
	},

	// Generate shareable link for a workflow
	generateWorkflowShareLink: async (workflow_id: string): Promise<{
		share_url: string;
		share_token: string;
		workflow_id: string;
	}> => {
		const response = await api.post<{
			share_url: string;
			share_token: string;
			workflow_id: string;
		}>("/api/v1/compliance/workflows/generate-share-link", null, {
			params: { workflow_id },
		});
		return response.data;
	},

	// Revoke shareable link for a workflow
	revokeWorkflowShareLink: async (workflow_id: string): Promise<{ detail: string }> => {
		const response = await api.delete<{ detail: string }>(
			"/api/v1/compliance/workflows/revoke-share-link",
			{
				params: { workflow_id },
			}
		);
		return response.data;
	},

	// Get shared workflow by token (public access)
	getSharedWorkflow: async (share_token: string): Promise<{
		workflow_id: string;
		workflow_name: string;
		created_at: string | null;
		steps: Array<{
			id: string;
			step_number: number;
			title: string;
			description: string;
			status: string;
		}>;
	}> => {
		const response = await api.get<{
			workflow_id: string;
			workflow_name: string;
			created_at: string | null;
			steps: Array<{
				id: string;
				step_number: number;
				title: string;
				description: string;
				status: string;
			}>;
		}>(`/api/v1/compliance/workflows/shared/${share_token}`);
		return response.data;
	},

	// Get EUDAMED devices from EU API via backend proxy
	getEudamedDevices: async (params: EudamedDevicesRequest): Promise<any> => {
		const response = await api.get("/api/v1/eudamed/devices", { params });
		return response.data;
	},

	// Update chat session status
	updateChatSessionStatus: async (
		sessionId: string,
		status: string
	): Promise<{
		session_id: string;
		status: string;
		message: string;
	}> => {
		const response = await api.patch<{
			session_id: string;
			status: string;
			message: string;
		}>(`/api/v1/chat/sessions/${sessionId}/status`, null, {
			params: { status },
		});
		return response.data;
	},

	// Fetch chat sessions with pagination
	getChatSessionsPaginated: async (
		page: number = 1,
		limit: number = 10
	): Promise<{
		sessions: ChatSession[];
		pagination: {
			page: number;
			limit: number;
			total: number;
			pages: number;
			has_next: boolean;
			has_prev: boolean;
		};
	}> => {
		const response = await api.get<{
			sessions: ChatSession[];
			pagination: {
				page: number;
				limit: number;
				total: number;
				pages: number;
				has_next: boolean;
				has_prev: boolean;
			};
		}>("/api/v1/chat/sessions", {
			params: { page, limit },
		});
		return response.data;
	},

	// Create a new session
	createNewSession: async (): Promise<{ session_id: string }> => {
		const response = await api.post<{ session_id: string }>(
			"/api/v1/session/new"
		);
		return response.data;
	},

	// Update password
	updatePassword: async (
		currentPassword: string,
		newPassword: string
	): Promise<{ message: string }> => {
		const response = await api.post<{ message: string }>(
			"/api/v1/auth/update-password",
			{
				current_password: currentPassword,
				new_password: newPassword,
			}
		);
		return response.data;
	},

	// Get all users (admin only)
	getAllUsers: async (): Promise<User[]> => {
		const response = await api.get<User[]>("/admin/users");
		return response.data;
	},

	// Update user (admin only)
	updateUser: async (
		userId: string,
		data: UserUpdateRequest
	): Promise<{ message: string }> => {
		const response = await api.put<{ message: string }>(
			`/admin/users/${userId}`,
			data
		);
		return response.data;
	},

	// Get user subscriptions (admin only)
	getUserSubscriptions: async (userId: string): Promise<any[]> => {
		const response = await api.get<any[]>(
			`/admin/users/${userId}/subscriptions`
		);
		return response.data;
	},

	// Get user payments (admin only)
	getUserPayments: async (userId: string): Promise<any[]> => {
		const response = await api.get<any[]>(`/admin/users/${userId}/payments`);
		return response.data;
	},

	// Send payment success email (admin only)
	sendPaymentEmail: async (data: {
		user_id: string;
		subscription_id: string;
		amount: number;
		currency: string;
		plan_name: string;
	}): Promise<{ message: string }> => {
		const response = await api.post<{ message: string }>(
			"/admin/send-payment-email",
			data
		);
		return response.data;
	},

	// Purchase tokens
	purchaseTokens: async (data: {
		amount: number;
	}): Promise<{
		message: string;
		success: boolean;
		tokens_added: number;
		new_balance: number;
	}> => {
		const response = await api.post<{
			message: string;
			success: boolean;
			tokens_added: number;
			new_balance: number;
		}>("/api/v1/profile/purchase-tokens", data);
		return response.data;
	},

	// Submit feedback for an AI response
	submitFeedback: async (
		feedback: FeedbackRequest
	): Promise<FeedbackResponse> => {
		const response = await api.post<FeedbackResponse>("/feedback", feedback);
		return response.data;
	},

	// Get feedback data with optional section filtering
	getFeedback: async (
		section?: string,
		limit: number = 50,
		offset: number = 0
	): Promise<FeedbackListResponse> => {
		const params = new URLSearchParams();
		if (section) params.append("section", section);
		params.append("limit", limit.toString());
		params.append("offset", offset.toString());

		const response = await api.get<FeedbackListResponse>(
			`/feedback?${params.toString()}`
		);
		return response.data;
	},

	// Get invitation details
	getInvitationDetails: async (token: string): Promise<InvitationDetails> => {
		const response = await api.get<InvitationDetails>(
			`/organizations/invitations/${token}/details`
		);
		return response.data;
	},

	// Accept invitation
	acceptInvitation: async (token: string): Promise<{ message: string }> => {
		const response = await api.post<{ message: string }>(
			`/organizations/invitations/${token}/accept`
		);
		return response.data;
	},

	// Decline invitation
	declineInvitation: async (token: string): Promise<{ message: string }> => {
		const response = await api.post<{ message: string }>(
			`/organizations/invitations/${token}/decline`
		);
		return response.data;
	},
};

// Regulation interfaces
export interface Regulation {
	id: string;
	regulation_code: string;
	regulation_name: string;
	short_name?: string;
	description?: string;
	summary?: string;
	country: string;
	jurisdiction: string;
	regulatory_authority?: string;
	applicable_device_classes: string[];
	applicable_device_types: string[];
	is_ivd_specific: boolean;
	is_md_specific: boolean;
	version?: string;
	effective_date?: string;
	expiry_date?: string;
	publication_date?: string;
	is_current: boolean;
	superseded_by?: string;
	supersedes?: string;
	official_url?: string;
	official_document_url?: string;
	reference_urls: string[];
	related_forms: string[];
	related_checklists: string[];
	key_requirements: string[];
	compliance_areas: string[];
	status: string;
	priority: number;
	tags: string[];
	metadata: Record<string, any>;
	created_at: string;
	updated_at: string;
	created_by?: string;
	is_active: boolean;
}

export interface RegulationCreateRequest {
	regulation_code: string;
	regulation_name: string;
	short_name?: string;
	description?: string;
	summary?: string;
	country: string;
	jurisdiction: string;
	regulatory_authority?: string;
	applicable_device_classes?: string[];
	applicable_device_types?: string[];
	is_ivd_specific?: boolean;
	is_md_specific?: boolean;
	version?: string;
	effective_date?: string;
	expiry_date?: string;
	publication_date?: string;
	is_current?: boolean;
	superseded_by?: string;
	supersedes?: string;
	official_url?: string;
	official_document_url?: string;
	reference_urls?: string[];
	related_forms?: string[];
	related_checklists?: string[];
	key_requirements?: string[];
	compliance_areas?: string[];
	status?: string;
	priority?: number;
	tags?: string[];
	metadata?: Record<string, any>;
}

export interface RegulationUpdateRequest extends Partial<RegulationCreateRequest> {}

// Regulations API
export const regulationsApi = {
	// Get all regulations
	getAllRegulations: async (params?: {
		jurisdiction?: string;
		status?: string;
		search?: string;
		page?: number;
		limit?: number;
	}): Promise<{ regulations: Regulation[]; total: number }> => {
		const queryParams = new URLSearchParams();
		if (params?.jurisdiction) queryParams.append("jurisdiction", params.jurisdiction);
		if (params?.status) queryParams.append("status", params.status);
		if (params?.search) queryParams.append("search", params.search);
		if (params?.page) queryParams.append("page", params.page.toString());
		if (params?.limit) queryParams.append("limit", params.limit.toString());
		
		const response = await api.get<{ regulations: Regulation[]; total: number }>(
			`/api/v1/regulations?${queryParams.toString()}`
		);
		return response.data;
	},

	// Get regulation by ID
	getRegulation: async (id: string): Promise<Regulation> => {
		const response = await api.get<Regulation>(`/api/v1/regulations/${id}`);
		return response.data;
	},

	// Create regulation
	createRegulation: async (data: RegulationCreateRequest): Promise<Regulation> => {
		const response = await api.post<Regulation>("/api/v1/regulations", data);
		return response.data;
	},

	// Update regulation
	updateRegulation: async (id: string, data: RegulationUpdateRequest): Promise<Regulation> => {
		const response = await api.put<Regulation>(`/api/v1/regulations/${id}`, data);
		return response.data;
	},

	// Delete regulation
	deleteRegulation: async (id: string): Promise<{ message: string }> => {
		const response = await api.delete<{ message: string }>(`/api/v1/regulations/${id}`);
		return response.data;
	},

	// Get all unique jurisdictions from regulations
	getJurisdictions: async (): Promise<string[]> => {
		const response = await api.get<string[]>("/api/v1/regulations/jurisdictions");
		return response.data;
	},

	// Detect country from IP and get matching regulatory authority
	detectCountryFromIP: async (): Promise<{
		country: string | null;
		regulatory_authority: string | null;
		regulation_id: string | null;
		regulation_code?: string;
	}> => {
		const response = await api.get<{
			country: string | null;
			regulatory_authority: string | null;
			regulation_id: string | null;
			regulation_code?: string;
		}>("/api/v1/regulations/detect-country");
		return response.data;
	},

	// Get public regulatory authorities (for dropdown - accessible to all authenticated users)
	getPublicRegulatoryAuthorities: async (): Promise<{
		regulations: Regulation[];
		total: number;
	}> => {
		const response = await api.get<{
			regulations: Regulation[];
			total: number;
		}>("/api/v1/regulations/public/authorities");
		return response.data;
	},
};

export default api;
